import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';

/* Breadcrumbs — the path home, read off the URL. A slim strip under the
 * header: 🏠 › Inventory › Purchase, current page bold. Ids and the
 * scaffolding segments (/restaurant/7/locations/2/…) are silent: a guest
 * reads WHERE they are, not how the router spells it.
 */

/* Segment → label, for slugs whose title-case isn't the product's word. */
const LABELS = {
  'qr-codes': 'QR Codes',
  'menu-management': 'All Menus',
  menus: 'Menus',
  'add-dish': 'Add Dish',
  'edit-dish': 'Edit Dish',
  crm: 'CRM',
  'bar-inventory': 'Bar Inventory',
  'liquor-management': 'Liquor Management',
  'liquor-tracking': 'Liquor Tracking',
  'food-cost': 'Food Cost',
  buffets: 'Open Buffets',
  'buffet-templates': 'Buffet List',
  'live-counters': 'Live Counters',
  masters: 'Master Data',
  'floor-plan': 'Floor Plan',
  'table-management': 'Tables',
  'all-reservations': 'All Reservations',
  'self-service': 'Self Service',
  pos: 'POS',
  help: 'Help & InTutor',
  website: 'Website Settings',
  places: 'Places (Near me)',
};

/* Router plumbing that never reads as a place: the segment AND, for the
   paired ones, the id that follows it. */
const SCAFFOLDING = new Set(['restaurant', 'locations', 'location']);

const labelFor = (seg) => LABELS[seg]
  || seg.replace(/-/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());

/* `inline` puts the trail INSIDE the header row beside the user chip, rather
   than on a strip of its own under it — the same words, one row less of
   chrome, which is the whole point of moving it up there. */
export default function Breadcrumbs({ inline = false }) {
  const { pathname } = useLocation();
  if (pathname === '/' || pathname === '/login') return null;

  const raw = pathname.split('/').filter(Boolean);
  const crumbs = [];
  for (let i = 0; i < raw.length; i += 1) {
    const seg = decodeURIComponent(raw[i]);
    if (SCAFFOLDING.has(seg)) {
      // Swallow the scaffold word and the id riding behind it.
      if (/^\d+$/.test(raw[i + 1] || '')) i += 1;
      continue;
    }
    if (/^\d+$/.test(seg)) continue; // a bare id names nothing
    crumbs.push({ seg, label: labelFor(seg) });
  }
  if (!crumbs.length) return null;

  return (
    <nav aria-label="Breadcrumb"
      className={inline
        /* In the header: no rule, no ground of its own, and it gives way
           first — the trail is context, not a headline, so it truncates
           before it pushes the search and the icons off the row. */
        ? 'hidden md:flex items-center gap-1.5 text-sm min-w-0 overflow-hidden'
        : 'flex items-center gap-1.5 border-b border-border bg-card/60 px-4 py-1.5 text-sm shrink-0'}>
      <Link to="/" aria-label="Home"
        className="shrink-0 text-muted-foreground transition-colors hover:text-foreground">
        <Home size={14} />
      </Link>
      {crumbs.map((c, i) => (
        <React.Fragment key={`${c.seg}-${i}`}>
          <ChevronRight size={13} className="shrink-0 text-muted-foreground/50" />
          {i === crumbs.length - 1 ? (
            <span className="font-semibold text-foreground truncate">{c.label}</span>
          ) : (
            /* Only the leaf is worth the width when the row is tight. */
            <span className={`text-muted-foreground truncate${inline ? ' hidden lg:inline' : ''}`}>
              {c.label}
            </span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}
